/**
 * Shared state for the opening-details page.
 *
 * Two hooks:
 *  - `useOpeningContext()` — non-suspending. Returns local UI state, the URL
 *    `hiringGoalId`, and optional metrics window params (`start_date`,
 *    `end_date`, `filter_range`). Sibling sections use this for query keys so their
 *    fetches fire in parallel with (not after) the funnel-details API.
 *  - `useOpeningDetails()` — suspending. Performs `useSuspenseQuery` for the
 *    funnel-details API and exposes `opening`, `hiringGoal`, `recentPastHiringGoal`,
 *    `analyticsData`. Each consumer suspends at its own nearest <Suspense> boundary,
 *    sibling sections can render their own skeletons during the initial load
 *    instead of the whole page going blank.
 *
 * Pref toggles that participate in the funnel query key
 * (`showAggregateHires`/`showAggregateSpend`) should trigger updates through
 * `AggregateDataCheckbox`, which wraps changes in `startTransition` locally
 * so the funnel area does not flash back to skeletons while queries refetch.
 */

import { useSuspenseQuery } from '@tanstack/react-query';
import {
  SourcingHiringGoalWithMetrics,
  SourcingMetricsFilterRange,
  SourcingOpeningDetailAnalyticsData,
  SourcingOpeningFilterDuration,
  SourcingService,
  SourcingSetting,
  SourcingStatus,
} from 'api-clients/monolith';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import { useHireUrl } from '@/hooks/use-hire-url';
import { useHiringGoalIdParam } from '@/hooks/use-hiring-goal-id-param';
import { useMetricsFilterParams } from '@/hooks/use-metrics-filter-params';
import { useOpeningSlug } from '@/hooks/use-opening-slug';
import { queryKeys } from '@/query-keys';

import type { MetricsViewMode } from '@/components/pages/opening-details/overview/metrics-view-toggle';

type OpeningSlice = {
  id: string;
  title: string;
  /** URL to edit the opening in the recruiter UI. */
  url: string;
  brandSlug: string | null;
  locationId: string;
  isActive: boolean;
  isSourcingEnabled: boolean;
  sourcingStatus: SourcingStatus;
  automation: SourcingSetting | null;
  hasActiveHiringGoal: boolean;
  defaultMetricsPeriod: SourcingMetricsFilterRange | null;
};

type DisplayPrefsState = {
  showAggregateData: boolean;
  showAggregateHires: boolean;
  showAggregateSpend: boolean;
};

type DisplayPrefsSlice = DisplayPrefsState & {
  setDisplayPrefs: (partial: Partial<DisplayPrefsState>) => void;
};

type OpeningContextValue = {
  displayPrefs: DisplayPrefsSlice;
  /** Overview metrics tab: hiring goal vs date range. */
  metricsViewMode: MetricsViewMode;
  /**
   * Switch tab and sync URL filter params.
   *
   * - `'hiring_goal'`  → always clears URL metrics params.
   * - `'date_range'` with `defaultPeriod` provided → writes that range to the
   *   URL (pass `null` for lifetime which clears params instead).
   * - `'date_range'` with `defaultPeriod` omitted → UI-only state update; use
   *   when the URL already has the correct params (e.g. initial-load sync).
   */
  setMetricsViewMode: (
    mode: MetricsViewMode,
    defaultPeriod?: SourcingMetricsFilterRange | null,
  ) => void;
};

type OpeningContextType = OpeningContextValue & {
  /**
   * Hiring-goal id from the URL `?hiring_goal_id` param. Only present when a
   * past hiring goal is selected; `undefined` for the current goal so the
   * backend can resolve it. Use this in sibling query keys instead of the
   * funnel-derived id so those fetches don't wait on the funnel API.
   */
  hiringGoalId: string | undefined;
  /** Inclusive metrics window start (`start_date` query param), if present. */
  startDate: string | undefined;
  /** Inclusive metrics window end (`end_date` query param), if present. */
  endDate: string | undefined;
  /** Preset metrics window (`filter_range` query param), if present. */
  filterRange: SourcingMetricsFilterRange | undefined;
};

type OpeningDetailsType = {
  opening: OpeningSlice;
  /** Hiring goal with metrics data, when returned by the openings API. */
  /** This may hold a dummy goal with nil external_id and placeholder data for the opening-details page. */
  hiringGoal: SourcingHiringGoalWithMetrics | null;
  /** Most recently ended hiring goal, when returned by the openings API. */
  recentPastHiringGoal: SourcingHiringGoalWithMetrics | null;
  analyticsData: SourcingOpeningDetailAnalyticsData;
  /** Applied metrics window returned by the openings API. */
  filterDuration: SourcingOpeningFilterDuration;
  /** URL to edit the hiring goal in the recruiter UI. */
  hiringGoalEditUrl: string;
};

const OpeningContext = createContext<OpeningContextValue | undefined>(
  undefined,
);

/**
 * Holds local UI state for the opening-details page. Does NOT fetch data
 * itself — `useOpeningDetails()` does that lazily via `useSuspenseQuery`,
 * so each consumer suspends against its own nearest Suspense boundary.
 */
export function OpeningProvider({ children }: { children: React.ReactNode }) {
  const [displayPrefsState, setDisplayPrefsState] = useState<DisplayPrefsState>(
    {
      showAggregateData: false,
      showAggregateHires: false,
      showAggregateSpend: false,
    },
  );

  const setDisplayPrefs = useCallback(
    (partial: Partial<DisplayPrefsState>) =>
      setDisplayPrefsState(prev => ({ ...prev, ...partial })),
    [],
  );

  const [metricsViewMode, setMetricsViewModeState] =
    useState<MetricsViewMode>('hiring_goal');

  const { clearMetricsFilter, applyFilterRange } = useMetricsFilterParams();

  const setMetricsViewMode = useCallback(
    (
      mode: MetricsViewMode,
      defaultPeriod?: SourcingMetricsFilterRange | null,
    ) => {
      setMetricsViewModeState(mode);
      if (mode === 'hiring_goal') {
        clearMetricsFilter();
      } else if (defaultPeriod !== undefined) {
        if (defaultPeriod === null) {
          clearMetricsFilter(); // lifetime: let the API use org defaults
        } else {
          applyFilterRange(defaultPeriod);
        }
      }
      // defaultPeriod === undefined → UI-only sync; URL already correct
    },
    [clearMetricsFilter, applyFilterRange],
  );

  const value = useMemo<OpeningContextValue>(
    () => ({
      displayPrefs: { ...displayPrefsState, setDisplayPrefs },
      metricsViewMode,
      setMetricsViewMode,
    }),
    [displayPrefsState, setDisplayPrefs, metricsViewMode, setMetricsViewMode],
  );

  return (
    <OpeningContext.Provider value={value}>{children}</OpeningContext.Provider>
  );
}

/**
 * Non-suspending hook for local UI state and the URL-derived hiring-goal id.
 * Safe to call from sibling-query consumers that need a stable query key
 * before the funnel-details API resolves.
 */
export function useOpeningContext(): OpeningContextType {
  const context = useContext(OpeningContext);
  if (!context) {
    throw new Error('useOpeningContext must be used within an OpeningProvider');
  }

  const { hiringGoalId } = useHiringGoalIdParam();
  const { startDate, endDate, filterRange } = useMetricsFilterParams();

  return useMemo(
    () => ({
      hiringGoalId,
      startDate,
      endDate,
      filterRange,
      displayPrefs: context.displayPrefs,
      metricsViewMode: context.metricsViewMode,
      setMetricsViewMode: context.setMetricsViewMode,
    }),
    [hiringGoalId, startDate, endDate, filterRange, context],
  );
}

/**
 * Suspending hook for funnel-details data (opening, hiring goal, analytics).
 * Consumers that call this must be wrapped in a <Suspense> boundary; React
 * Query dedupes the fetch across sibling consumers.
 */
export function useOpeningDetails(): OpeningDetailsType {
  const openingSlug = useOpeningSlug();
  const { hiringGoalId, startDate, endDate, filterRange, displayPrefs } =
    useOpeningContext();
  const { getHireATHiringGoalUrl, getHireOpeningEditUrl } = useHireUrl();
  const { showAggregateHires, showAggregateSpend } = displayPrefs;

  const { data: funnel } = useSuspenseQuery({
    queryKey: [
      ...queryKeys.openingDetails.detail,
      openingSlug,
      hiringGoalId,
      showAggregateHires,
      showAggregateSpend,
      startDate,
      endDate,
      filterRange,
    ],
    queryFn: () =>
      SourcingService.getInternalApiSourcingOpenings(
        openingSlug,
        hiringGoalId,
        showAggregateHires,
        showAggregateSpend,
        startDate,
        endDate,
        filterRange,
      ),
    // `useOpeningDetails()` is called independently by several sibling
    // components (Overview, ChannelsSection, AutomationStatusBanner,
    // ChannelsContent) that all mount around the same time. With the
    // default staleTime of 0, whichever of them mounts a moment after
    // another has already resolved this query sees "stale" data and
    // triggers a redundant duplicate fetch. A short staleTime lets those
    // sibling mounts share the one fetch instead of each firing their own.
    staleTime: 10 * 1000, // 10 seconds
  });

  return useMemo(() => {
    const hiringGoalUrl = getHireATHiringGoalUrl(
      funnel.brand_slug ?? 'no_brand',
      funnel.location_id,
      funnel.opening_id,
    );

    return {
      opening: {
        id: funnel.opening_id,
        title: funnel.title,
        url: getHireOpeningEditUrl(funnel.opening_id),
        brandSlug: funnel.brand_slug,
        locationId: funnel.location_id,
        isActive: funnel.active,
        isSourcingEnabled: funnel.is_sourcing_funnel,
        sourcingStatus: funnel.sourcing_status,
        automation: funnel.automation ?? null,
        hasActiveHiringGoal: funnel.has_active_hiring_goal,
        defaultMetricsPeriod:
          funnel.default_metrics_period != null
            ? (funnel.default_metrics_period.toString() as SourcingMetricsFilterRange)
            : null,
      },
      hiringGoalEditUrl: hiringGoalUrl,
      hiringGoal: funnel.hiring_goal,
      recentPastHiringGoal: funnel.recent_past_hiring_goal,
      analyticsData: funnel.analytics,
      filterDuration: funnel.filter_duration,
    };
  }, [funnel, getHireATHiringGoalUrl, getHireOpeningEditUrl]);
}
